import { createIntl, createIntlCache } from 'react-intl';

import { APIConfig } from 'src/config';
import { DEFAULT_LOCALE, getDefaultLocale, i18nMessagesForLocale } from 'src/util/i18n/handler';

// Partner locale is the configured locale for public/config.js.
const partnerLocale = APIConfig()?.LOCALE || DEFAULT_LOCALE;

// Prevents memory leak per recommendation by react-intl.
const cache = createIntlCache();

// Load the locale messages.
const messages = {
  ...i18nMessagesForLocale(getDefaultLocale(partnerLocale)),
  ...i18nMessagesForLocale(partnerLocale),
};

const intl = createIntl(
  {
    locale: partnerLocale,
    messages,
  },
  cache,
);

/**
 * Returns the appropriate currency symbol for a given locale
 * This function supports the holistic tariff time unit approach by providing
 * locale-specific currency symbols for complete rate expressions.
 *
 * Supports all locales from SUPPORTED_DEFAULT_LOCALES: ['en', 'en-AU', 'en-GB',
 * 'en-US', 'it', 'ja', 'fr-FR']
 * @param {string} locale - The locale string (e.g., 'en-AU', 'en-GB', 'ja-JP', 'it-IT', 'fr-FR')
 * @returns {string} The currency symbol for the locale:
 *   - 'en-AU', 'en-US', 'en' → '$' (Australian/US Dollar)
 *   - 'en-GB' → '£' (British Pound)
 *   - 'ja-JP', 'ja' → '¥' (Japanese Yen)
 *   - 'it-IT', 'it' → '€' (Euro)
 *   - 'fr-FR', 'fr' → '€' (Euro)
 *   - Default fallback → '$' (Australian Dollar)
 */
export const getCurrencySymbolByLocale = (locale) => {
  if (!locale || typeof locale !== 'string') {
    return '$'; // Default fallback to AUD symbol
  }

  // Normalize locale to handle various formats (e.g., 'ja-JP-x-partner' -> 'ja-JP')
  const normalizedLocale = locale.split('-x-')[0].toLowerCase();

  // Map locales to their appropriate currency symbols based on SUPPORTED_DEFAULT_LOCALES

  // Check for British English locales (including variants like en-GB-extra)
  if (normalizedLocale === 'en-gb' || normalizedLocale.startsWith('en-gb-')) {
    return '£'; // British Pound (GBP)
  }

  // Check for Japanese locales
  if (normalizedLocale.startsWith('ja-') || normalizedLocale === 'ja') {
    return '¥'; // Japanese Yen (JPY)
  }

  // Euro zone locales (Italy and France)
  const euroLocales = ['it', 'fr'];

  if (euroLocales.some((euroLocale) => normalizedLocale === euroLocale || normalizedLocale.startsWith(`${euroLocale}-`))) {
    return '€'; // Euro (EUR)
  }

  // Check for other English locales (en-AU, en-US, en) and default fallback
  if (normalizedLocale.startsWith('en-') || normalizedLocale === 'en') {
    return '$'; // Australian/US Dollar (AUD/USD) - treating as equivalent for display
  }

  // Default fallback for any unsupported locales
  return '$'; // Australian Dollar (AUD) - default fallback
};

export const useIntlCurrency = () => intl;
